import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUserId } from "../../services/authService";
import { getCategories } from "../../services/categoryService";
import {
  changeFromRecurringToTransaction,
  changeFromTransactionToRecurring,
  getRecurringTransactionByIdFlexible,
  getTransactionByIdFlexible,
  getVendors,
  updateRecurringTransaction,
  updateTransaction,
} from "../../services/transactionService";
import { useGroupContext } from "../Groups/useGroupContext";
import { formatDateForEdit } from "../utils/dateUtils";
import TransactionForm from "./TransactionForm";

const EditTransaction = (props) => {
  const { id } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [originalContext, setOriginalContext] = useState(null);
  const isRecurring = props.isRecurring;
  const navigate = useNavigate();
  const { getContextParams, switchToGroup, switchToPersonal, isGroupMode } =
    useGroupContext();

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        const data = await getTransactionByIdFlexible(id);

        setOriginalContext({
          type: data.found_in_context,
          groupId: data.group_id,
          groupName: data.group_name,
        });

        setTransaction({
          ...data,
          date: formatDateForEdit(data.date),
          old_groupId: data.group_id,
          old_groupName: data.group_name,
        });
      } catch (error) {
        console.error("Error fetching transaction:", error);
        alert("Transaction not found or access denied");
        navigate("/expenses");
      }
    };

    const fetchRecurringTransaction = async () => {
      try {
        const data = await getRecurringTransactionByIdFlexible(id);

        setOriginalContext({
          type: data.found_in_context,
          groupId: data.group_id,
          groupName: data.group_name,
        });

        const isPermanent = !data.end_date;
        setTransaction({
          ...data,
          date: formatDateForEdit(data.start_date),
          next_due_date: formatDateForEdit(data.next_due_date),
          end_date: data.end_date ? formatDateForEdit(data.end_date) : "",
          start_date: formatDateForEdit(data.start_date),
          isPermanent: isPermanent,
          isRecurring: true,
          recurringcount: isPermanent ? "" : data.recurringcount || "",
          old_groupId: data.group_id,
          old_groupName: data.group_name,
          isInstallments: data.total_amount ? true : false,
        });
      } catch (error) {
        console.error("Error fetching recurring transaction:", error);
        alert("Recurring transaction not found or access denied");
        navigate("/recurring-payments");
      }
    };

    const fetchCategories = async () => {
      try {
        const contextParams = getContextParams();
        const categoriesData = await getCategories(contextParams);
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    const fetchVendors = async () => {
      try {
        const contextParams = getContextParams();
        const vendorsList = await getVendors(contextParams);
        setVendors(vendorsList);
      } catch (error) {
        console.error("Error fetching vendors list:", error);
      }
    };

    fetchCategories();
    fetchVendors();
    if (!isRecurring) {
      fetchTransaction();
    } else {
      fetchRecurringTransaction();
    }
  }, [id, isRecurring, getContextParams]);

  const handleUpdate = async (updatedTransaction) => {
    const userId = await getUserId();
    if (!userId) {
      alert("User not authenticated");
      return;
    }

    const contextParams = getContextParams();

    const category = categories.find(
      (cat) =>
        cat.name === updatedTransaction.category_name &&
        cat.type === updatedTransaction.category_type
    );

    if (!category) {
      alert("Category not found!");
      return;
    }
    updatedTransaction.category_id = category.id;

    if (updatedTransaction.isRecurring) {
      updatedTransaction.start_date = updatedTransaction.date;
      updatedTransaction.next_due_date = calculateEndDate(
        updatedTransaction.date,
        updatedTransaction.frequency,
        2
      );
      updatedTransaction.end_date = updatedTransaction.isPermanent
        ? null
        : calculateEndDate(
            updatedTransaction.date,
            updatedTransaction.frequency,
            updatedTransaction.recurringcount
          );
      if (isRecurring) {
        const response = await updateRecurringTransaction(
          updatedTransaction.id,
          updatedTransaction,
          contextParams
        );
        if (response.status !== 200 && response.status !== 201) {
          alert("Failed to update recurring transaction.");
        }
      } else {
        const response = await changeFromTransactionToRecurring(
          updatedTransaction.id,
          updatedTransaction,
          contextParams
        );
        if (response.status !== 200 && response.status !== 201) {
          alert("Failed to update recurring transaction.");
        }
      }

      navigate("/recurring-payments");
    } else {
      if (isRecurring) {
        const response = await changeFromRecurringToTransaction(
          updatedTransaction.id,
          updatedTransaction,
          contextParams
        );
        if (response.status === 200 || response.status === 201) {
          alert(`Transaction updated successfully!`);
          navigate(
            updatedTransaction.category_type === "INCOME"
              ? "/income"
              : updatedTransaction.category_type === "INVESTMENT"
              ? "/investments"
              : updatedTransaction.isRecurring
              ? "/recurring-payments"
              : "/expenses"
          );
        } else {
          alert("Failed to update transaction.");
        }
      } else {
        const response = await updateTransaction(
          updatedTransaction.id,
          updatedTransaction,
          contextParams
        );
        if (response.status === 200 || response.status === 201) {
          alert(`Transaction updated successfully!`);
          navigate(
            updatedTransaction.category_type === "INCOME"
              ? "/income"
              : updatedTransaction.category_type === "INVESTMENT"
              ? "/investments"
              : updatedTransaction.isRecurring
              ? "/recurring-payments"
              : "/expenses"
          );
        } else {
          alert("Failed to update transaction.");
        }
      }
    }
    //navigate(props.returnTo);
  };

  const calculateEndDate = (date, frequency, recurringcount) => {
    if (!date || !recurringcount) return null;

    const startDate = new Date(date);
    const count = parseInt(recurringcount, 10) - 1;

    if (isNaN(count) || count <= 0) return null;

    switch (frequency) {
      case "DAILY":
        startDate.setDate(startDate.getDate() + count);
        break;
      case "WEEKLY":
        startDate.setDate(startDate.getDate() + count * 7);
        break;
      case "MONTHLY":
        startDate.setMonth(startDate.getMonth() + count);
        break;
      case "YEARLY":
        startDate.setFullYear(startDate.getFullYear() + count);
        break;
      default:
        return null;
    }
    startDate.setHours(12, 0, 0, 0);
    return startDate.toISOString().split("T")[0];
  };

  return transaction ? (
    <TransactionForm
      isEdit={true}
      data={transaction}
      categories={categories}
      vendors={vendors}
      onSubmit={handleUpdate}
      initialRecurring={isRecurring}
    />
  ) : (
    <p>Loading...</p>
  );
};

export default EditTransaction;
